import { downloadDocument } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DocViewerComponent,
  Icons,
  useToggle,
} from '@commenda/kakashi';
import { FileBasic } from '@commenda/types/src/file/types';
import { getFileTypeByUrl } from '@commenda/utils';
import { downloadDocumentLocally } from '@commenda/utils/documents/download-document';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';
import { useEffect, useState } from 'react';

export const PreviewItemModal = ({
  isOpen,
  onClose,
  previewFile,
}: {
  isOpen: boolean;
  onClose: () => void;
  previewFile: FileBasic;
}) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const isDownloadingFile = useToggle(false);

  useEffect(() => {
    if (!previewFile) return;

    const getFileUrl = async () => {
      const signedUrl = await getSignedUrlByFileId(
        previewFile.id as number,
        null
      );
      setFileUrl(signedUrl);
    };

    getFileUrl();
  }, [previewFile]);

  async function downloadFile() {
    try {
      isDownloadingFile.show();
      const { fileName } = previewFile;
      const fileType = getFileTypeByUrl(fileUrl);

      const blob = await downloadDocument({
        fileId: previewFile.id as number,
        fileType,
      });

      await downloadDocumentLocally({
        blob,
        fileName,
        fileType,
      });

      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      isDownloadingFile.hide();
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="flex flex-col gap-4 max-w-5xl">
        <DialogHeader className="flex flex-col items-start gap-4">
          <div className="flex flex-col gap-1">
            <DialogTitle>{previewFile?.fileName || 'Preview file'}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="h-[500px] overflow-y-auto p-6">
          {fileUrl && <DocViewerComponent uri={fileUrl} />}
        </div>

        <DialogFooter>
          <div className="flex justify-end w-full gap-2">
            <Button variant="secondary" onClick={window.print} size="sm">
              <Icons.Printer className="size-4" />
              Print
            </Button>
            <Button
              variant="primary"
              onClick={downloadFile}
              size="sm"
              disabled={isDownloadingFile.visible}
            >
              {isDownloadingFile.visible ? (
                <Icons.Loading01 className="size-4 animate-spin" />
              ) : (
                <Icons.Download01 className="size-4" />
              )}
              Download
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
